import { styled } from 'styled-components';
import { DatePicker, getDaysAround, MonthPicker, removePastDatesFromRange, tk } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';

import { useDepartureDateFilter } from './hooks';
import { add } from 'date-fns';
import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilter/types';
import * as React from 'react';
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider';
import showWeeks from '@/scopes/search/domain/showWeeks';

export function DatesCalendar({ options, className }: { options: DepartureDateFilterOptions; className?: string }) {
  const intl = useIntl();
  const { isDesktopSm } = useBreakpoints();
  const siteName = useConfiguration('siteName');

  const { availableDates, departureDate, flexibleRange, originDate, setOriginDate, setDepartureDate } =
    useDepartureDateFilter({ options });

  function setSelectedDate(date: string) {
    setOriginDate(date);

    if (flexibleRange !== 'exact') {
      const numberOfDays = parseInt(flexibleRange);
      const dateRange = getDaysAround(date, numberOfDays);
      const minimDepartureDate = add(new Date(), { days: 1 });
      const filteredDates = removePastDatesFromRange(dateRange, minimDepartureDate);
      setDepartureDate(filteredDates);
      return;
    }

    setDepartureDate(date);
  }

  React.useEffect(() => {
    if (!originDate && departureDate) {
      setOriginDate(Array.isArray(departureDate) ? departureDate?.[0] : departureDate);
    }
  }, []);

  const showWeekNumbers = showWeeks(siteName);

  return (
    <StyledDatePicker
      className={className}
      availableDates={availableDates}
      selectedDate={originDate ?? (Array.isArray(departureDate) ? departureDate?.[0] : departureDate)}
      startDate={Array.isArray(departureDate) ? departureDate[0] : undefined}
      endDate={Array.isArray(departureDate) ? departureDate[1] : undefined}
      locale={intl.locale}
      setSelectedDate={setSelectedDate}
      nextMonthAriaLabel={intl.formatMessage({ id: 'quicksearch_filter_departure_date_next_month' })}
      previousMonthAriaLabel={intl.formatMessage({ id: 'quicksearch_filter_departure_date_previous_month' })}
      numberOfMonths={isDesktopSm ? 2 : 1}
      showWeekNumbers={showWeekNumbers}
      weekLabel={showWeekNumbers ? intl.formatMessage({ id: 'departure_date_filter_week' }) : ''}
    />
  );
}

export function MonthCalendar({ options, className }: { options: DepartureDateFilterOptions; className?: string }) {
  const intl = useIntl();
  const { availableDates, maxDateString, minDateString, departureDate, setDepartureDate, setOriginDate } =
    useDepartureDateFilter({
      options,
    });

  function setSelectedDate(dates: string | [string, string]) {
    setOriginDate(Array.isArray(dates) ? dates[0] : dates);
    setDepartureDate(dates);
  }

  return (
    <StyledMonthPicker
      locale={intl.locale}
      availableDates={availableDates}
      maxDateString={maxDateString}
      minDateString={minDateString}
      selectedMonth={departureDate}
      setSelectedDates={setSelectedDate}
      className={className}
    />
  );
}

const StyledDatePicker = styled(DatePicker)`
  @media ${tk('mq_tablet')} {
    ${DatePicker.modSize('tablet')}
  }

  @media ${tk('mq_desktop_sm')} {
    ${DatePicker.modSize('desktop')}
  }
`;

const StyledMonthPicker = styled(MonthPicker)`
  @media ${tk('mq_desktop_sm')} {
    margin-bottom: ${tk('spacing_xl')};
    margin-top: ${tk('spacing_3xl')};
    width: 45rem;
  }
`;
